import { useUpdatePageModuleStatusMutation } from "entities/pages/mutations/useUpdatePageModuleStatusMutation";
import { useModuleListByBrandPageQuery } from "entities/pages/queries/useModuleListByBrandPageQuery";
import { usePagesListByBrandQuery } from "entities/pages/queries/usePagesListByBrandQuery";
import { PageModuleData, PageModuleStatus } from "entities/pages/types";
import { ModulesView, PagesTabs } from "modules/brands/context/types";
import { FC, PropsWithChildren, ReactNode, useCallback, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { useDynamicRouting } from "shared/hooks/useDynamicRouting";
import { useLocalSearch } from "shared/hooks/useLocalSearch";
import { useModalState } from "shared/hooks/useModalState";
import { BRAND_TAB_PATH } from "shared/routes/paths";
import { createContext } from "shared/utils/context";
import { MoveOrDuplicateType } from "../types";
import { PageModulesType } from "./types";

const [Provider, usePageModules] = createContext<PageModulesType>({
  name: "PageModulesContext",
  hookName: "usePageModules",
  providerName: "PageModulesProvider",
});

const PAGE_SIZE = 20;

const PageModulesProvider: FC<PropsWithChildren> = ({ children }) => {
  const { id, tabDetailsId: tabDetails } = useParams();
  const { pathname } = useLocation();

  const [activeTab, setActiveTab] = useState<PagesTabs>(PagesTabs.Preview);
  const [activeView, setActiveView] = useState<ModulesView>(ModulesView.List);

  const pageId = String(tabDetails);
  const brandId = String(id);

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useModuleListByBrandPageQuery(brandId, pageId, PAGE_SIZE);

  const { dynamicRoutes } = useDynamicRouting();

  const moduleDetailsDrawer = useDrawerState<PageModuleData>();
  const moveOrDuplicateModalState = useModalState();
  const [moveOrDuplicateType, setMoveOrDuplicateType] =
    useState<MoveOrDuplicateType>(MoveOrDuplicateType.Move);
  const [moveOrDuplicateModule, setMoveOrDuplicateModule] =
    useState<PageModuleData | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<ReactNode>("");
  const [alertIsSuccess, setAlertIsSuccess] = useState(false);
  const { mutate: updateModuleStatus } = useUpdatePageModuleStatusMutation({
    brandId,
    pageId,
  });

  const onUpdateStatus = async ({
    status,
    moduleId,
  }: {
    status: PageModuleStatus;
    moduleId: string;
  }) => {
    updateModuleStatus({ status, moduleId });
  };

  const loadMoreModules = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const { data: pages } = usePagesListByBrandQuery(brandId);

  const modules = data?.modules || [];

  const searchData = useLocalSearch({
    searchableFields: ["name"],
    initialData: modules,
  });

  const groupPageTitle = dynamicRoutes.reduce((acc, next) => {
    if (next.pathname === BRAND_TAB_PATH) {
      const groupTitle = next.title;
      const subGroupTitle =
        next.elements?.find((element) => pathname.includes(element.pathname))
          ?.title ?? "";

      return `${groupTitle} \u2022 ${subGroupTitle}`;
    }

    return acc;
  }, "");

  const modulePageTitle =
    pages?.find((module) => module.id === pageId)?.title ?? "";

  const value = {
    data: modules,
    isLoadingPages: isLoading,
    isRefetchingPages: isFetching,
    loadMoreModules,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    modulePageTitle,
    groupPageTitle,
    moduleDetailsDrawer,
    onUpdateStatus,
    brandId,
    pageId: String(pageId),
    searchData: {
      value: searchData.value,
      onChange: searchData.onChange,
      filteredData: searchData.filteredData,
      isSearchActive: Boolean(searchData.value),
    },
    activeTab,
    setActiveTab,
    moveOrDuplicateModalState,
    moveOrDuplicateType,
    setMoveOrDuplicateType,
    moveOrDuplicateModule,
    setMoveOrDuplicateModule,
    alertOpen,
    setAlertOpen,
    alertMessage,
    setAlertMessage,
    alertIsSuccess,
    setAlertIsSuccess,
    activeView,
    setActiveView,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { PageModulesProvider, usePageModules };
